import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';

import * as updatedProductStore from '@cartwhl/common/stores/updatedProductStore';
import * as userStore from '@cartwhl/common/stores/userStore';
import { isActiveFreeTrial } from '@cartwhl/common/utils/organizationWorkspace';

type PurchaseCreditsModalComponent = typeof import('./PurchaseCreditsModal').default;

let purchaseCreditsModalImport:
  | Promise<{ default: PurchaseCreditsModalComponent }>
  | undefined;

function importPurchaseCreditsModal() {
  purchaseCreditsModalImport ??= import('./PurchaseCreditsModal').catch(
    (error) => {
      purchaseCreditsModalImport = undefined;
      throw error;
    },
  );
  return purchaseCreditsModalImport;
}

/** Mount once per app (e.g. next to other global modals) so `openPurchaseCreditsModal` can open the UI from anywhere. */
export default function PurchaseCreditsModalHost() {
  const snap = useSnapshot(updatedProductStore.purchaseCreditsModalState);
  const { organizationInfo } = useSnapshot(userStore.state);
  const inStripeFreeTrial = isActiveFreeTrial(organizationInfo);
  const [PurchaseCreditsModal, setPurchaseCreditsModal] =
    useState<PurchaseCreditsModalComponent | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (inStripeFreeTrial) {
      updatedProductStore.closePurchaseCreditsModal();
    }
  }, [inStripeFreeTrial]);

  useEffect(() => {
    if (!snap.isOpen || inStripeFreeTrial || PurchaseCreditsModal) return;
    void loadModal();
  }, [snap.isOpen, inStripeFreeTrial, PurchaseCreditsModal]);

  function loadModal() {
    setLoadError(false);
    return importPurchaseCreditsModal()
      .then(({ default: ModalComponent }) => {
        setPurchaseCreditsModal(() => ModalComponent);
      })
      .catch(() => {
        setLoadError(true);
      });
  }

  if (!snap.isOpen || inStripeFreeTrial) {
    return null;
  }
  if (!PurchaseCreditsModal) {
    return (
      <div
        className="bg-primary border-primary fixed bottom-4 left-4 z-[70] flex max-w-[calc(100vw-2rem)] items-center gap-3 rounded-lg border p-3 shadow-menu"
        role={loadError ? 'alert' : 'status'}
        aria-live="polite"
      >
        <span className="text-primary">
          {loadError
            ? 'Unable to load credit purchase.'
            : 'Loading credit purchase…'}
        </span>
        {loadError && (
          <>
            <button
              type="button"
              className="text-primary hover:text-secondary rounded-md underline focus-visible:outline-2 focus-visible:outline-offset-2"
              onClick={() => void loadModal()}
            >
              Retry
            </button>
            <button
              type="button"
              className="text-primary hover:text-secondary rounded-md underline focus-visible:outline-2 focus-visible:outline-offset-2"
              onClick={updatedProductStore.closePurchaseCreditsModal}
            >
              Close
            </button>
          </>
        )}
        {!loadError && (
          <button
            type="button"
            className="text-primary hover:text-secondary rounded-md underline focus-visible:outline-2 focus-visible:outline-offset-2"
            onClick={updatedProductStore.closePurchaseCreditsModal}
          >
            Cancel
          </button>
        )}
      </div>
    );
  }
  return (
    <PurchaseCreditsModal
      close={updatedProductStore.closePurchaseCreditsModal}
      payload={{
        variant: snap.variant,
        creditsRemaining: snap.creditsRemaining,
      }}
    />
  );
}
